import Icon from "../../common/Icon";
import {
  DetailsContainer,
  PanelDetailsContainer,
  StatusActions,
  StatusDetails,
  StatusIcon,
  StatusTable,
  StatusTableCell,
  StatusTableContainer,
  StatusTableHeader,
  StatusTableRow,
  StencilPanelDetailsRow,
  VerticalList,
} from "./BoardInventoryStyles";
import StencilInfo from "./StencilInfo";
import PanelInfo from "./PanelInfo";
import React, { useContext, useState } from "react";
import {
  Board,
  ManufacturingStepStatus,
  MountType,
  Shipment,
} from "../../../reducers/uploadedBoard";
import Modal from "../../common/Modal";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import useBoardBin from "../../../utils/hooks/commonly-stored/uploaded-board/boardBin";
import BoardBinActions from "./BoardBinActions";
import BinInfo from "./BinInfo";
import { FakeLink } from "../../../constants/Styles";
import PartStatusDetails from "./PartStatusDetails";
import useBoardInventory, {
  FeederStatus,
  FeederStatusResponse,
  useContainersNotOnFeedersForBoard,
} from "../../../utils/hooks/inventory/boardInventory";
import ContainersOnFeedersStatusDetails from "./containersOnFeedersStatus";
import { ProcessingPill } from "../../ReviewBoardFlow/ProjectDetails";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import LoadingIcon from "../../common/LoadingIcon";
import useGetStencilsForProject from "../../../utils/hooks/inventory/getStencilsForProject";
import SelectStencil from "./SelectStencil";
import useGetPanelsForProject from "../../../utils/hooks/inventory/getPanelsForProject";
import SelectPanel from "./SelectPanels";
import AddOrUpdateInventoryItem from "../parts/AddOrUpdateInventoryItem";
import { InventoryItemType } from "../../../utils/hooks/inventory/useInventoryItems";
import useShipmentsFromContext from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";

export default function StatusSummaryTable() {
  const { shipmentId } = useContext(BoardIdContext) as BoardShipmentIds;
  const board = useBoardFromContext().board as Board;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const [stencilCheckIn, setStencilCheckIn] = useState<boolean>(false);
  const [panelCheckIn, setPanelCheckIn] = useState<boolean>(false);
  const [selectStencil, setSelectStencil] = useState<boolean>(false);
  const [selectPanel, setSelectPanel] = useState<boolean>(false);
  const [viewPartStatusDetails, setViewPartStatusDetails] =
    useState<boolean>(false);
  const [
    viewContainersNeededOnFeedersTop,
    setViewContainersNeededOnFeedersTop,
  ] = useState<boolean>(false);
  const [
    viewContainersNeededOnFeedersBottom,
    setViewContainersNeededOnFeedersBottom,
  ] = useState<boolean>(false);
  const [viewBinActions, setViewBinActions] = useState<boolean>(false);
  const { inventoryBin: boardBin } = useBoardBin(board.id);
  const shipment = shipments.find((shipment) => {
    return shipment.id == shipmentId;
  });
  const { stencilsForProject, refreshStencilsForProject } =
    useGetStencilsForProject(board.id);
  const { panelsForProject, refreshPanelsForProject } = useGetPanelsForProject(
    board.id,
  );

  const closeModal = () => {
    setPanelCheckIn(false);
    setStencilCheckIn(false);
    setSelectStencil(false);
    setSelectPanel(false);
    setViewPartStatusDetails(false);
    setViewBinActions(false);
    setViewContainersNeededOnFeedersTop(false);
    setViewContainersNeededOnFeedersBottom(false);
  };

  const { boardPartsResponse } = useBoardInventory(
    board.id,
    shipment?.id || null,
  );

  const { containersNeededOnFeeders } = useContainersNotOnFeedersForBoard(
    board.id,
    shipment?.id || null,
  );

  if (shipment === undefined) {
    return <LoadingIcon />;
  }

  const boardIsBlank = shipment.shipment_parts.length === 0;
  const partsWithAllocationsSatisfyingTheRequiredAmount =
    shipment.shipment_parts.reduce((numAllocated, shipmentPart) => {
      if (shipmentPart.amount_allocated >= shipmentPart.required_amount) {
        numAllocated += 1;
      }
      return numAllocated;
    }, 0) ?? 0;

  const partsWithAllocationsSatisfyingThePlacementCount =
    shipment.shipment_parts.reduce((numAllocated, shipmentPart) => {
      if (
        shipmentPart.amount_allocated < shipmentPart.required_amount &&
        shipmentPart.amount_allocated >= shipmentPart.num_elements_per_shipment
      ) {
        numAllocated += 1;
      }
      return numAllocated;
    }, 0) ?? 0;

  const partsWithInsufficientQuantity =
    shipment.shipment_parts.reduce((numInsufficient, shipmentPart) => {
      if (
        shipmentPart.amount_allocated < shipmentPart.num_elements_per_shipment
      ) {
        numInsufficient += 1;
      }
      return numInsufficient;
    }, 0) ?? 0;

  const getBoardPartsStatus = () => {
    if (boardIsBlank) {
      return "Board does not require assembly.";
    } else {
      if (
        partsWithAllocationsSatisfyingTheRequiredAmount >=
        shipment.shipment_parts.length
      ) {
        return "All parts are available in inventory";
      } else {
        return (
          <VerticalList>
            {partsWithAllocationsSatisfyingThePlacementCount > 0 && (
              <div>{`${partsWithAllocationsSatisfyingThePlacementCount} part(s) with quantities below the required amount with spillage / attrition`}</div>
            )}
            {partsWithInsufficientQuantity > 0 && (
              <div>{`${partsWithInsufficientQuantity} part(s) with quantities below the placement count for this board`}</div>
            )}
            <FakeLink onClick={() => setViewPartStatusDetails(true)}>
              View part status details
            </FakeLink>
          </VerticalList>
        );
      }
    }
  };

  const getBoardCapacity = () => {
    if (boardPartsResponse === null) {
      return <ProcessingPill />;
    } else {
      let maxBoards = Infinity;
      for (const part of boardPartsResponse.parts) {
        const numPartsInInventory =
          part.inventory_response.physical_inventory.last_counted_amount_total -
          part.inventory_response.physical_inventory.spent_allocations_total;
        const numBoardsCoveredByThisPart = Math.floor(
          numPartsInInventory / part.num_elements,
        );
        maxBoards = Math.min(maxBoards, numBoardsCoveredByThisPart);
      }
      return (
        <div>{`${maxBoards} / ${shipment.num_boards} boards manufacturable with the current inventory`}</div>
      );
    }
  };

  const thereAreTopOrBottomSMTParts = (feederStatus: FeederStatus) => {
    return (
      feederStatus.containers_in_board_tote.length > 0 ||
      feederStatus.containers_not_on_feeders_or_in_tote.length > 0 ||
      feederStatus.containers_on_feeders.length > 0 ||
      feederStatus.parts_not_smt_ready.length > 0
    );
  };

  const getBoardPartsOnFeedersStatus = (
    feederStatus: FeederStatus | null,
    boardSide: MountType,
  ) => {
    if (feederStatus === null) {
      return <ProcessingPill />;
    }
    const someAllocatedContainersAreStillInBoardTote =
      feederStatus.containers_in_board_tote.length > 0;
    const someAllocatedContainersAreStillInGeneralInventory =
      feederStatus.containers_not_on_feeders_or_in_tote.length > 0;
    const someAllocatedContainersAreOnFeeders =
      feederStatus.containers_on_feeders.length > 0;
    const allPartsMeetSMTReadyRequirements =
      feederStatus.parts_not_smt_ready.length === 0;

    if (!thereAreTopOrBottomSMTParts(feederStatus)) {
      return `No allocated SMT ${boardSide} parts for this project`;
    }
    if (allPartsMeetSMTReadyRequirements) {
      return "All parts have at least one allocated container on feeders";
    } else {
      return (
        <VerticalList>
          {someAllocatedContainersAreOnFeeders && (
            <div>{`${feederStatus.containers_on_feeders.length} container(s) are on feeders.`}</div>
          )}
          {someAllocatedContainersAreStillInBoardTote && (
            <div>{`${feederStatus.containers_in_board_tote.length} container(s) with allocations for this board are still in the board tote.`}</div>
          )}
          {someAllocatedContainersAreStillInGeneralInventory && (
            <div>{`${feederStatus.containers_not_on_feeders_or_in_tote.length} containers(s) for this project are still in general inventory.`}</div>
          )}
        </VerticalList>
      );
    }
  };

  const getPartsOnFeedersStatusIcon = (
    containersNeededOnFeeders: FeederStatusResponse | null,
    mount_side: MountType,
  ) => {
    if (containersNeededOnFeeders === null) {
      return <ProcessingPill />;
    }
    if (mount_side === MountType.TOP) {
      if (thereAreTopOrBottomSMTParts(containersNeededOnFeeders.top)) {
        if (containersNeededOnFeeders.top.parts_not_smt_ready.length === 0) {
          return <Icon src={"/icons/check_green.svg"} alt={"check"} />;
        } else {
          return <Icon src={"/icons/fatal.svg"} alt={"error"} />;
        }
      } else {
        return <Icon src={"/icons/remove_gray.svg"} alt={"none"} />;
      }
    }

    if (thereAreTopOrBottomSMTParts(containersNeededOnFeeders.bottom)) {
      if (containersNeededOnFeeders.bottom.parts_not_smt_ready.length === 0) {
        return <Icon src={"/icons/check_green.svg"} alt={"check"} />;
      } else {
        return <Icon src={"/icons/fatal.svg"} alt={"error"} />;
      }
    } else {
      return <Icon src={"/icons/remove_gray.svg"} alt={"none"} />;
    }
  };

  const renderStencilDetails = () => {
    if (stencilsForProject === null) {
      return <LoadingIcon text={"Loading stencil details..."} />;
    } else if (
      stencilsForProject &&
      stencilsForProject.inventory_items.length > 0
    ) {
      return stencilsForProject.inventory_items.map((stencil) => (
        <StencilPanelDetailsRow key={stencil.flashpcb_container_barcode}>
          <StencilInfo
            stencil={stencil}
            boardBin={boardBin}
            boardId={board.id}
            onUpdate={refreshStencilsForProject}
          />
        </StencilPanelDetailsRow>
      ));
    } else {
      return <div>No stencils associated with this project yet.</div>;
    }
  };

  const renderPanelDetails = () => {
    if (panelsForProject === null) {
      return <LoadingIcon text={"Loading panel details..."} />;
    } else if (
      panelsForProject &&
      panelsForProject.inventory_items.length > 0
    ) {
      return panelsForProject.inventory_items.map((panel) => (
        <StencilPanelDetailsRow key={panel.flashpcb_container_barcode}>
          <PanelInfo
            panel={panel}
            boardBin={boardBin}
            boardId={board.id}
            onUpdate={refreshPanelsForProject}
          />
        </StencilPanelDetailsRow>
      ));
    } else {
      return <div>No panels associated with this project yet.</div>;
    }
  };

  return (
    <StatusTableContainer>
      <StatusTable>
        <thead>
          <tr>
            <StatusTableHeader>Item</StatusTableHeader>
            <StatusTableHeader>Status</StatusTableHeader>
            <StatusTableHeader>Details</StatusTableHeader>
          </tr>
        </thead>
        <tbody>
          <StatusTableRow>
            <StatusTableCell>Board Bin</StatusTableCell>
            <StatusTableCell>
              <StatusIcon>
                {boardBin ? (
                  <Icon src={"/icons/check_green.svg"} alt={"check"} />
                ) : (
                  <Icon src={"/icons/fatal.svg"} alt={"error"} />
                )}
              </StatusIcon>
            </StatusTableCell>
            <StatusTableCell>
              <StatusDetails>
                {boardBin ? (
                  <BinInfo
                    binName={boardBin.bin_name}
                    binLocation={boardBin.bin_location}
                    binBarcode={boardBin.flashpcb_bin_barcode}
                    binType={boardBin.bin_type}
                    canAllocateFromBin={boardBin.can_allocate_from_this_bin}
                  />
                ) : (
                  "No board bin created"
                )}
                <FakeLink onClick={() => setViewBinActions(true)}>
                  Bin actions
                </FakeLink>
              </StatusDetails>
            </StatusTableCell>
          </StatusTableRow>

          {!boardIsBlank && (
            <StatusTableRow>
              <StatusTableCell>Parts Inventory</StatusTableCell>
              <StatusTableCell>
                <StatusIcon>
                  {shipment.shipment_parts &&
                  partsWithAllocationsSatisfyingTheRequiredAmount ===
                    shipment.shipment_parts.length ? (
                    <Icon src={"/icons/check_green.svg"} alt={"check"} />
                  ) : (
                    <Icon src={"/icons/fatal.svg"} alt={"error"} />
                  )}
                </StatusIcon>
              </StatusTableCell>
              <StatusTableCell>
                <StatusDetails>
                  <VerticalList>
                    {getBoardPartsStatus()}
                    {getBoardCapacity()}
                  </VerticalList>
                </StatusDetails>
              </StatusTableCell>
            </StatusTableRow>
          )}

          {!boardIsBlank && (
            <StatusTableRow>
              <StatusTableCell>TOP Parts On Feeders</StatusTableCell>
              <StatusTableCell>
                <StatusIcon>
                  {getPartsOnFeedersStatusIcon(
                    containersNeededOnFeeders,
                    MountType.TOP,
                  )}
                </StatusIcon>
              </StatusTableCell>
              <StatusTableCell>
                <StatusDetails>
                  {getBoardPartsOnFeedersStatus(
                    containersNeededOnFeeders
                      ? containersNeededOnFeeders.top
                      : null,
                    MountType.TOP,
                  )}
                  {containersNeededOnFeeders &&
                    thereAreTopOrBottomSMTParts(
                      containersNeededOnFeeders.top,
                    ) && (
                      <FakeLink
                        onClick={() =>
                          setViewContainersNeededOnFeedersTop(true)
                        }
                      >
                        View feeder status details
                      </FakeLink>
                    )}
                </StatusDetails>
              </StatusTableCell>
            </StatusTableRow>
          )}

          {!boardIsBlank && (
            <StatusTableRow>
              <StatusTableCell>BOTTOM Parts On Feeders</StatusTableCell>
              <StatusTableCell>
                <StatusIcon>
                  {getPartsOnFeedersStatusIcon(
                    containersNeededOnFeeders,
                    MountType.BOTTOM,
                  )}
                </StatusIcon>
              </StatusTableCell>
              <StatusTableCell>
                <StatusDetails>
                  {getBoardPartsOnFeedersStatus(
                    containersNeededOnFeeders
                      ? containersNeededOnFeeders.bottom
                      : null,
                    MountType.BOTTOM,
                  )}
                  {containersNeededOnFeeders &&
                    thereAreTopOrBottomSMTParts(
                      containersNeededOnFeeders.bottom,
                    ) && (
                      <FakeLink
                        onClick={() =>
                          setViewContainersNeededOnFeedersBottom(true)
                        }
                      >
                        View feeder status details
                      </FakeLink>
                    )}
                </StatusDetails>
              </StatusTableCell>
            </StatusTableRow>
          )}

          {!boardIsBlank && (
            <StatusTableRow>
              <StatusTableCell>Part Placement Info</StatusTableCell>
              <StatusTableCell>
                <StatusIcon>
                  {shipment.manufacturing_checklist
                    .num_parts_missing_placement_type === 0 ? (
                    <Icon src={"/icons/check_green.svg"} alt={"check"} />
                  ) : (
                    <Icon src={"/icons/fatal.svg"} alt={"error"} />
                  )}
                </StatusIcon>
              </StatusTableCell>
              <StatusTableCell>
                <StatusDetails>
                  {shipment.manufacturing_checklist
                    .num_parts_missing_placement_type === 0
                    ? null
                    : `${shipment.manufacturing_checklist.num_parts_missing_placement_type} parts missing placement type`}
                </StatusDetails>
              </StatusTableCell>
            </StatusTableRow>
          )}

          {!boardIsBlank && (
            <StatusTableRow>
              <StatusTableCell>Stencil</StatusTableCell>
              <StatusTableCell>
                <StatusIcon>
                  {shipment.manufacturing_checklist.inventory_status.stencil ==
                  ManufacturingStepStatus.READY ? (
                    <Icon src={"/icons/check_green.svg"} alt={"check"} />
                  ) : (
                    <Icon src={"/icons/fatal.svg"} alt={"error"} />
                  )}
                </StatusIcon>
              </StatusTableCell>
              <StatusTableCell>
                <StatusDetails>
                  <>
                    Board element mount types:{" "}
                    {
                      board.manufacturing_details.stencil_details
                        .board_mount_types
                    }
                    <DetailsContainer>
                      {renderStencilDetails()}
                      <StatusActions>
                        <FakeLink onClick={() => setStencilCheckIn(true)}>
                          Check in stencil
                        </FakeLink>
                        <FakeLink onClick={() => setSelectStencil(true)}>
                          Associate existing stencil with project
                        </FakeLink>
                      </StatusActions>
                    </DetailsContainer>
                  </>
                </StatusDetails>
              </StatusTableCell>
            </StatusTableRow>
          )}

          <StatusTableRow>
            <StatusTableCell>Panel</StatusTableCell>
            <StatusTableCell>
              <StatusIcon>
                {shipment.manufacturing_checklist.inventory_status.panel ==
                ManufacturingStepStatus.READY ? (
                  <Icon src={"/icons/check_green.svg"} alt={"check"} />
                ) : (
                  <Icon src={"/icons/fatal.svg"} alt={"error"} />
                )}
              </StatusIcon>
            </StatusTableCell>
            <StatusTableCell>
              <StatusDetails>
                <PanelDetailsContainer>
                  {renderPanelDetails()}
                  <FakeLink onClick={() => setPanelCheckIn(true)}>
                    Check in panel
                  </FakeLink>
                  <FakeLink onClick={() => setSelectPanel(true)}>
                    Associate existing panel with project
                  </FakeLink>
                </PanelDetailsContainer>
              </StatusDetails>
            </StatusTableCell>
          </StatusTableRow>
        </tbody>
      </StatusTable>
      {panelCheckIn && (
        <Modal isOpen={panelCheckIn} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <AddOrUpdateInventoryItem
              itemType={InventoryItemType.PANELS}
              itemToUpdate={null}
              onCancel={closeModal}
              onSuccess={closeModal}
              boardId={board.id}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {stencilCheckIn && (
        <Modal isOpen={stencilCheckIn} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <AddOrUpdateInventoryItem
              itemType={InventoryItemType.STENCIL}
              itemToUpdate={null}
              onCancel={closeModal}
              onSuccess={closeModal}
              boardId={board.id}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {selectStencil && (
        <Modal isOpen={true} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <SelectStencil boardId={board.id} closeModal={closeModal} />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {selectPanel && (
        <Modal isOpen={true} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <SelectPanel boardId={board.id} closeModal={closeModal} />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {viewPartStatusDetails && (
        <Modal isOpen={viewPartStatusDetails} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <PartStatusDetails board={board} />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {viewBinActions && (
        <Modal isOpen={viewBinActions} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal} fullScreen>
            <BoardBinActions board={board} onFinishedEditing={closeModal} />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      {viewContainersNeededOnFeedersTop &&
        containersNeededOnFeeders !== null && (
          <Modal
            isOpen={viewContainersNeededOnFeedersTop}
            onRequestClose={closeModal}
          >
            <ModalContentWithCloseButton closeModal={closeModal}>
              <ContainersOnFeedersStatusDetails
                board={board}
                shipmentParts={shipment.shipment_parts}
                containersNeededOnFeeders={containersNeededOnFeeders.top}
              />
            </ModalContentWithCloseButton>
          </Modal>
        )}
      {viewContainersNeededOnFeedersBottom &&
        containersNeededOnFeeders !== null && (
          <Modal
            isOpen={viewContainersNeededOnFeedersBottom}
            onRequestClose={closeModal}
          >
            <ModalContentWithCloseButton closeModal={closeModal}>
              <ContainersOnFeedersStatusDetails
                board={board}
                shipmentParts={shipment.shipment_parts}
                containersNeededOnFeeders={containersNeededOnFeeders.bottom}
              />
            </ModalContentWithCloseButton>
          </Modal>
        )}
    </StatusTableContainer>
  );
}
